// AGC: AddTeamMember - Form to add new team member with job title
// AGC: Visual Refresh Design System
// AGC: Supports role selection, job title, email invitation

import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { useRBAC } from '../../hooks/useRBAC'
import { useSubscription } from '../../context/SubscriptionContext'
import { useFarms } from '../../context'
import { getTeamMemberCount } from '../../services/firebase-auth'
import { ROLE_INFO, getRoleLevel, type FarmRole } from '../../types/rbac'
import { getBackendUrl } from '../../utils/backendUrl'
import { PageHeader } from '../shared/PageHeader'
import { BottomNav } from '../shared'
import { useFormTelemetry } from '../../hooks/useFormTelemetry'
import { track } from '../../lib/analytics'
import { UserPlus, Mail, Shield, ArrowLeft, Loader2, Crown, AlertCircle, Camera, User, Phone, Copy, Check, MessageCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import type { Screen } from '../../types'

const BASE_URL = getBackendUrl()

interface AddTeamMemberProps {
  onBack: () => void
  onNavigate: (screen: Screen, params?: any) => void
}

/**
 * AGC: AddTeamMember - Form to invite new team member
 * AGC: Features:
 * AGC: - Email input for invitation
 * AGC: - Job title field (e.g., "Maquinista", "Gerente", "Peón")
 * AGC: - Role selection (owner/manager/worker)
 * AGC: - Visual role cards with permissions preview
 */
export const AddTeamMember: React.FC<AddTeamMemberProps> = ({ onBack, onNavigate }) => {
  const { t } = useTranslation()
  const { getAuthToken, user } = useAuth()
  const { role: myFarmRole } = useRBAC()
  // AGC: Form telemetry — tracks worker_add form submit success/fail
  const { trackSubmit } = useFormTelemetry('worker_add')
  const { tier, isLoading: subLoading, subscription } = useSubscription()
  const { currentFarm, farms, isLoading: isFarmsLoading } = useFarms()

  // AGC: Form state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [jobTitle] = useState('') // AGC: kept for API compat, no longer user-facing
  const [selectedRole, setSelectedRole] = useState<FarmRole>('worker')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // AGC: Invite result state
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // AGC: Roles filtered by hierarchy — can only invite roles below yours
  const myLevel = getRoleLevel(myFarmRole || 'owner')
  const invitableRoles: FarmRole[] = (['manager', 'agronomist', 'financial', 'worker', 'viewer'] as FarmRole[])
    .filter(r => getRoleLevel(r) < myLevel)
  
  // AGC: Subscription state
  const [memberCount, setMemberCount] = useState<number>(0)
  const [isLoadingCount, setIsLoadingCount] = useState(true)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  // AGC: Load team member count and check subscription limits
  // AGC: subscription===null means data hasn't arrived yet (tier defaults to 'free')
  // AGC: IMPORTANT: must also reset showUpgradeModal to false when tier is not blocked
  useEffect(() => {
    const loadCountAndCheckLimits = async () => {
      if (!user?.uid || subLoading || !subscription) return

      try {
        setIsLoadingCount(true)
        const count = await getTeamMemberCount(user.uid)
        setMemberCount(count)

        // AGC: Check subscription limits — set or RESET the modal
        if (tier === 'free') {
          setShowUpgradeModal(true)
        } else if (tier === 'pro' && count >= 5) {
          setShowUpgradeModal(true)
        } else {
          setShowUpgradeModal(false)
        }
      } catch (error) {
        console.error('AGC: Error loading team member count:', error)
      } finally {
        setIsLoadingCount(false)
      }
    }

    loadCountAndCheckLimits()
  }, [user?.uid, tier, subLoading, subscription])
  
  
  // AGC: Handle avatar upload
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    // AGC: Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('team.errors.invalidImageType') || 'Por favor, selecione uma imagem')
      return
    }
    
    // AGC: Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('team.errors.imageTooLarge') || 'Imagem muito grande (máx 5MB)')
      return
    }
    
    setAvatarFile(file)
    
    // AGC: Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }
  
  // AGC: Trigger file input
  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // AGC: Validation
    if (!firstName.trim()) {
      toast.error(t('team.errors.firstNameRequired') || 'Nome é obrigatório')
      return
    }
    
    if (!lastName.trim()) {
      toast.error(t('team.errors.lastNameRequired') || 'Sobrenome é obrigatório')
      return
    }
    
    if (!email.trim()) {
      toast.error(t('team.errors.emailRequired') || 'Email é obrigatório')
      return
    }
    
    if (!email.includes('@')) {
      toast.error(t('team.errors.emailInvalid') || 'Email inválido')
      return
    }
    
    try {
      setIsSubmitting(true)

      // AGC: Resolve farmId — fall back to first farm if currentFarm not set yet
      const farmId = currentFarm?.id || farms[0]?.id
      if (!farmId) {
        toast.error(t('team.errors.noFarm') || 'Nenhuma fazenda selecionada')
        return
      }

      const token = await getAuthToken?.()
      if (!token) {
        toast.error(t('team.errors.authRequired') || 'Autenticação necessária')
        return
      }

      // AGC: Call new farm-scoped invite endpoint, wrapped in form telemetry
      const workerInvitedAt = Date.now()
      const data = await trackSubmit(async () => {
        const res = await fetch(`${BASE_URL}/api/v1/farms/${farmId}/team/invite`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            email: email.trim(),
            role: selectedRole,
            displayName: `${firstName.trim()} ${lastName.trim()}`,
            phone: phone.trim() || undefined,
            jobTitle: selectedRole,
          }),
        })
        if (!res.ok) {
          const errData = await res.json()
          throw new Error(errData.message || t('team.errors.createFailed') || 'Erro ao enviar convite')
        }
        return res.json()
      })

      // AGC: Telemetry — worker entity invited successfully
      void track('entity_created', {
        entity_type: 'worker',
        source: 'form',
        duration_ms: Date.now() - workerInvitedAt,
      })

      // AGC: Show invite URL for manual sharing
      setInviteUrl(data.inviteUrl)
      const channels = [t('teamInvite.email') || 'email']
      if (phone.trim()) channels.push('WhatsApp')
      toast.success(`${t('team.inviteSent') || 'Convite enviado'} (${channels.join(' + ')})`)
    } catch (error: unknown) {
      console.error('AGC: Error creating invite:', error)
      const msg = error instanceof Error ? error.message : (t('team.errors.createFailed') || 'Erro ao enviar convite')
      toast.error(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const roleInfo = ROLE_INFO[selectedRole] || ROLE_INFO['worker']  // AGC: Fallback to worker if role not found
  
  // AGC: Determine upgrade message based on tier
  const upgradeMessage = tier === 'free' 
    ? (t('team.upgradeRequired.free') || 'Free tier cannot add team members. Upgrade to PRO.')
    : (t('team.upgradeRequired.proLimit') || 'PRO tier is limited to 5 members. Upgrade to PREMIUM for unlimited.')

  return (
    <div className="min-h-screen app-bg pb-20 transition-colors">
      {/* AGC: Upgrade Modal Overlay */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                <Crown className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('team.upgradeRequired.title') || 'Upgrade para Adicionar Membros'}
              </h2>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {upgradeMessage}
              </p>
              
              {tier === 'pro' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    {t('team.currentMembers') || 'Membros atuais'}: <strong>{memberCount}/5</strong>
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowUpgradeModal(false)
                  onBack()
                }}
                className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel') || 'Cancelar'}
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false)
                  onNavigate('subscription')
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-xl font-semibold hover:from-yellow-600 hover:to-yellow-700 transition-all flex items-center justify-center gap-2"
              >
                <Crown className="w-5 h-5" />
                {t('subscription.upgrade') || 'Upgrade'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* AGC: Header */}
      <PageHeader
        icon={UserPlus}
        title={t('team.addMember') || 'Adicionar Membro'}
        actions={
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            title={t('common.back') || 'Voltar'}
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
        }
      />

      <form onSubmit={handleSubmit} className="px-4 py-6 space-y-6">
        {/* AGC: Loading State */}
        {isLoadingCount && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
          </div>
        )}
        
        {/* AGC: Subscription Limit Warning — only show after subscription data exists */}
        {!isLoadingCount && subscription && (tier === 'free' || (tier === 'pro' && memberCount >= 4)) && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                  {tier === 'free' 
                    ? (t('team.upgradeRequired.title') || 'Upgrade Necessário')
                    : (t('team.nearLimit') || 'Próximo do Limite')
                  }
                </h4>
                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                  {tier === 'free'
                    ? (t('team.upgradeRequired.free') || 'Plano gratuito não permite adicionar membros. Faça upgrade para PRO.')
                    : (t('team.proLimitWarning') || `Você tem ${memberCount}/5 membros. Upgrade para PREMIUM para membros ilimitados.`)
                  }
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* AGC: Avatar Upload */}
        <div className="flex justify-center">
          <div className="relative">
            <button
              type="button"
              onClick={handleAvatarClick}
              className="relative w-24 h-24 rounded-full bg-gray-100 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-700 overflow-hidden hover:border-navy-500 transition-all group"
            >
              {avatarPreview ? (
                <img 
                  src={avatarPreview} 
                  alt="Avatar preview" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-12 h-12 text-gray-400 group-hover:text-navy-500 transition-colors" />
                </div>
              )}
              
              {/* AGC: Camera overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Camera className="w-8 h-8 text-white" />
              </div>
            </button>
            
            {/* AGC: Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleAvatarChange}
              className="hidden"
            />
            
            {/* AGC: Camera badge */}
            <div className="absolute bottom-0 right-0 w-8 h-8 bg-navy-600 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-900 shadow-lg">
              <Camera className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>
        
        <p className="text-xs text-center text-gray-500 dark:text-gray-400 -mt-3">
          {t('team.avatarHelp') || 'Toque na foto para adicionar imagem'}
        </p>
        
        {/* AGC: First Name Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {t('team.firstName') || 'Nome'}
              <span className="text-red-500">*</span>
            </div>
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={t('team.firstNamePlaceholder') || 'João'}
            className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all"
            required
          />
        </div>

        {/* AGC: Last Name Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {t('team.lastName') || 'Sobrenome'}
              <span className="text-red-500">*</span>
            </div>
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={t('team.lastNamePlaceholder') || 'Silva'}
            className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all"
            required
          />
        </div>
        
        {/* AGC: Email Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {t('team.email') || 'Email'}
              <span className="text-red-500">*</span>
            </div>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('team.emailPlaceholder') || 'colaborador@exemplo.com'}
            className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all"
            required
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('team.emailHelp') || 'Um convite será enviado para este email'}
          </p>
        </div>

        {/* AGC: Phone Field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              {t('team.phone') || 'Telefone'}
            </div>
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('team.phonePlaceholder') || '+55 (41) 99999-9999'}
            className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <MessageCircle className="w-3 h-3" />
            {t('team.phoneHelpWhatsApp') || 'WhatsApp — o convite também será enviado por mensagem (opcional)'}
          </p>
        </div>

        {/* AGC: Role Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              {t('team.role') || 'Função'}
              <span className="text-red-500">*</span>
            </div>
          </label>
          
          <div className="space-y-3">
            {invitableRoles.map((role) => {
              const info = ROLE_INFO[role]
              const isSelected = selectedRole === role
              
              return (
                <button
                  key={role}
                  type="button"
                  onClick={() => setSelectedRole(role)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-navy-500 bg-navy-50 dark:bg-navy-900/20'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{info.icon}</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {info.label}
                      </span>
                    </div>
                    {isSelected && (
                      <div className="w-5 h-5 bg-navy-500 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full" />
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {info.description}
                  </p>
                </button>
              )
            })}
          </div>
        </div>

        {/* AGC: Permission Preview */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
            {t('team.permissions') || 'Permissões'}
          </h4>
          <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
            {(roleInfo?.keyPermissions || []).map((permission, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>{permission}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* AGC: Invite URL success card (shown after successful invite) */}
        {inviteUrl && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-2">
              {t('team.inviteSentTitle') || 'Convite enviado!'}
            </h4>
            <p className="text-xs text-green-700 dark:text-green-300 mb-3">
              {t('team.inviteLinkHelp') || 'Compartilhe este link manualmente se necessário:'}
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={inviteUrl}
                className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-lg text-xs text-gray-700 dark:text-gray-300"
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(inviteUrl)
                  setCopied(true)
                  setTimeout(() => setCopied(false), 2000)
                }}
                className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}

        {/* AGC: Submit Button */}
        {!inviteUrl ? (
          <button
            type="submit"
            disabled={isSubmitting || !email}
            className="w-full bg-navy-700 hover:bg-navy-800 disabled:bg-gray-400 text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('team.sending') || 'Enviando convite...'}
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5" />
                {t('team.sendInvite') || 'Enviar Convite'}
              </>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={onBack}
            className="w-full bg-navy-700 hover:bg-navy-800 text-white font-semibold py-4 px-6 rounded-xl transition-colors"
          >
            {t('team.backToTeam') || 'Voltar para Equipe'}
          </button>
        )}
      </form>

      <BottomNav 
        active="team" 
        onNavigate={(tab) => {
          if (tab === 'dashboard') onNavigate('dashboard')
          else if (tab === 'lotes') onNavigate('lotes-list')
          else if (tab === 'tasks') onNavigate('tasks-list')
          else if (tab === 'weather') onNavigate('weather')
        }}
      />
    </div>
  )
}

export default AddTeamMember
